import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import type { Game } from '../../../types/game';
import { useGameRegistration } from '../../../hooks/useGameRegistration';
import { usePlayerToken } from '../../../hooks/usePlayerToken';
import { useShieldEligibility, useShieldStatus, useShieldToken } from '../../../hooks/useShieldStatus';
import { SlotBar } from '../gameParts';
import { Panel, UtButton, UtToggle } from '../primitives';
import { UtShieldSheet } from './UtShieldSheet';
import { UtSelectionExplainer } from './UtSelectionExplainer';

interface Props {
  game: Game;
  playerId: string | null;
  isUserRegistered: boolean;
  /** 1-based XP rank of the viewer among registrants (0 if not registered). */
  myRank: number;
  registeredCount: number;
  tokenCount: number;
  myUsingToken: boolean;
  isProcessing: boolean;
  onChanged: () => Promise<void>;
  useToken: boolean;
  setUseToken: (v: boolean) => void;
}

/**
 * Registration column: register / "you're registered" with drop out, the slot
 * bar, the priority token toggle, and the shield option for a week off.
 * Actions are the classic ones (useGameRegistration, usePlayerToken,
 * useShieldToken); only the presentation is new.
 */
export const RegistrationPanel: React.FC<Props> = ({
  game,
  playerId,
  isUserRegistered,
  myRank,
  registeredCount,
  tokenCount,
  myUsingToken,
  isProcessing,
  onChanged,
  useToken,
  setUseToken,
}) => {
  const { isRegistering, handleRegistration } = useGameRegistration({
    gameId: game.id,
    isUserRegistered,
    onRegistrationChange: onChanged,
    useToken,
    setUseToken,
  });
  const { shieldStatus, refreshShieldStatus } = useShieldStatus(playerId ?? undefined);
  const [explain, setExplain] = useState(false);
  const hasShieldForThisGame = shieldStatus?.activeShields?.some((s) => s.game_id === game.id) ?? false;

  const xpSlots = Math.max(0, (game.max_players ?? 0) - (game.random_slots ?? 0) - tokenCount);
  const randomSlots = game.random_slots ?? 0;

  const placeText = (() => {
    if (myUsingToken) return 'Your priority token guarantees you a place.';
    if (myRank > 0 && myRank <= xpSlots) return `The top ${xpSlots} get in on XP, so you're on course for a place.`;
    if (randomSlots > 0) return `The top ${xpSlots} get in on XP. You're in the draw for ${randomSlots} random ${randomSlots === 1 ? 'place' : 'places'}.`;
    return `The top ${xpSlots} get in on XP. You're on the reserve list for now.`;
  })();

  if (isProcessing) {
    return (
      <Panel className="flex flex-col gap-1 text-center">
        <span className="font-ut-display text-xl font-extrabold uppercase">Selecting players…</span>
        <span className="text-sm text-ut-mut">Hang on while the line-up is worked out.</span>
      </Panel>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {!playerId ? (
        <UtButton to="/login" size="xl" block>
          SIGN IN TO REGISTER
        </UtButton>
      ) : isUserRegistered ? (
        <Panel tone="accent" className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between gap-2.5">
            <span className="font-ut-display text-[22px] font-extrabold text-ut-acc">YOU&apos;RE REGISTERED</span>
            <UtButton variant="secondary" size="sm" onClick={handleRegistration} disabled={isRegistering}>
              {isRegistering ? 'Working…' : 'Drop out'}
            </UtButton>
          </div>
          <div className="grid grid-cols-[auto_1fr] items-center gap-3">
            <span className="font-ut-display text-[40px] font-extrabold leading-none">#{myRank || '–'}</span>
            <span className="text-sm leading-snug">
              of {registeredCount} by XP. {placeText}
            </span>
          </div>
        </Panel>
      ) : (
        <UtButton size="xl" block onClick={handleRegistration} disabled={isRegistering || hasShieldForThisGame}>
          {isRegistering ? 'WORKING…' : `REGISTER FOR #${game.sequence_number}`}
        </UtButton>
      )}

      {hasShieldForThisGame && (
        <Panel tone="accent" pad="sm" className="text-sm">
          You&apos;re using a shield token for this game. Cancel the shield to register instead.
        </Panel>
      )}

      <SlotBar registered={registeredCount} maxPlayers={game.max_players} xpSlots={xpSlots} randomSlots={randomSlots} />
      <button type="button" onClick={() => setExplain(true)} className="-mt-1 self-start text-[13px] font-semibold text-ut-acc hover:text-ut-ink">
        How selection works →
      </button>
      <UtSelectionExplainer
        open={explain}
        onClose={() => setExplain(false)}
        matchNumber={game.sequence_number}
        maxPlayers={game.max_players}
        randomSlots={randomSlots}
        tokenCount={tokenCount}
      />

      {playerId && !isUserRegistered && !hasShieldForThisGame && (
        <TokenOption playerId={playerId} value={useToken} onChange={setUseToken} disabled={isRegistering} />
      )}
      {playerId && isUserRegistered && myUsingToken && (
        <Panel pad="sm" className="flex items-center justify-between">
          <span className="text-sm font-semibold">Priority token on</span>
          <span className="text-xs text-ut-mut">Guarantees a slot</span>
        </Panel>
      )}

      {playerId && shieldStatus && (shieldStatus.tokensAvailable > 0 || hasShieldForThisGame) && (
        <ShieldOption
          gameId={game.id}
          playerId={playerId}
          matchNumber={game.sequence_number}
          tokensAvailable={shieldStatus.tokensAvailable}
          protectedStreak={shieldStatus.shieldActive ? shieldStatus.effectiveStreak : (shieldStatus.currentStreak ?? 0)}
          alreadyEasing={shieldStatus.shieldActive && !hasShieldForThisGame}
          active={hasShieldForThisGame}
          isRegistered={isUserRegistered}
          onChanged={async () => {
            await refreshShieldStatus();
            await onChanged();
          }}
        />
      )}
    </div>
  );
};

/** Priority token toggle row. Hidden when the player has no token or isn't eligible. */
const TokenOption: React.FC<{ playerId: string; value: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({
  playerId,
  value,
  onChange,
  disabled,
}) => {
  const { data, isLoading } = usePlayerToken(playerId);
  if (isLoading || !data?.hasToken || !data?.isEligible) return null;
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-ut-line bg-ut-card px-4 py-3">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold">{value ? 'Priority token on' : 'Use priority token'}</span>
        <span className="text-xs text-ut-mut">Guarantees a slot. 1 available.</span>
      </div>
      <UtToggle checked={value} onChange={onChange} disabled={disabled} label="Use priority token" />
    </div>
  );
};

/** "Can't play this week?" shield row opening the Use a shield sheet, or cancel when a shield is already on this game. */
const ShieldOption: React.FC<{
  gameId: string;
  matchNumber?: number | string;
  playerId: string;
  tokensAvailable: number;
  /** Streak use_shield_token would protect (effective streak while a shield is easing down). */
  protectedStreak: number;
  alreadyEasing: boolean;
  active: boolean;
  isRegistered: boolean;
  onChanged: () => Promise<void>;
}> = ({ gameId, matchNumber, playerId, tokensAvailable, protectedStreak, alreadyEasing, active, isRegistered, onChanged }) => {
  const [confirm, setConfirm] = useState(false);
  const { eligibility } = useShieldEligibility(playerId, gameId);
  const { useShield, returnShield, isUsing } = useShieldToken(playerId, gameId);

  const use = async () => {
    try {
      const r = await useShield();
      toast.success(`Shield token used! ${r.tokensRemaining} shield${r.tokensRemaining !== 1 ? 's' : ''} remaining.`);
      setConfirm(false);
      await onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to use shield token');
    }
  };
  const cancel = async () => {
    try {
      const r = await returnShield();
      toast.success(`Shield cancelled! ${r.tokensNow} shield${r.tokensNow !== 1 ? 's' : ''} available.`);
      await onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to cancel shield');
    }
  };

  // check_shield_eligibility says eligible for registered players too: using a shield cancels the registration.
  const canUse = (eligibility?.eligible ?? false) && tokensAvailable > 0 && !isUsing;

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-ut-line bg-ut-card px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold">{active ? 'Streak protected for this game' : "Can't play this week?"}</span>
          <span className="text-xs text-ut-mut">
            {active
              ? 'Your shield token is protecting your streak.'
              : `Use a shield token to protect your ${protectedStreak}-game streak. ${tokensAvailable} held.`}
          </span>
        </div>
        {active ? (
          <UtButton variant="danger" size="sm" onClick={cancel} disabled={isUsing}>
            Cancel shield
          </UtButton>
        ) : (
          <UtButton variant="secondary" size="sm" onClick={() => setConfirm(true)} disabled={!canUse}>
            Use shield
          </UtButton>
        )}
      </div>
      {!active && eligibility && !eligibility.eligible && eligibility.reason && <span className="text-xs text-ut-mut">{eligibility.reason}</span>}
      <UtShieldSheet
        open={confirm}
        onClose={() => setConfirm(false)}
        onConfirm={use}
        busy={isUsing}
        matchNumber={matchNumber}
        tokensAvailable={tokensAvailable}
        protectedStreak={protectedStreak}
        alreadyEasing={alreadyEasing}
        isRegistered={isRegistered}
      />
    </div>
  );
};
